import { Controller } from "@hotwired/stimulus"
import { showConfirmModal } from "../lib/confirm_modal.mjs"

// Confirms a send when the recipient's email domain isn't one the provider is known to use, so a
// contact filed against the wrong provider can't quietly take a patient's documents with it.
//
// The decision belongs to the server (DeliveryRecipientGuard). For a form whose recipient is known
// at render time this controller is only attached when the answer is already "warn", and carries
// the sentence to show. A form whose recipient can still change -- a picker, or an "add a new
// recipient" box -- carries a check URL and asks once, on submit.
//
// Put it on the <form> that does the sending; the attributes come from `delivery_guard_attributes`
// (app/helpers/delivery_guard_helper.rb):
//
//   <form ... <%= tag.attributes(delivery_guard_attributes(location, recipient: email.email)) %>>
//
// See docs/delivery_recipient_guard.md.
export default class extends Controller {
  static targets = ["recipient", "recipientOverride"]
  static values = {
    message: Array,
    checkUrl: String,
    providerUrl: String
  }

  connect() {
    // Set for exactly one re-submit after the person confirms; see `confirm`.
    this.confirmed = false
    this.asking = false
  }

  async confirm(event) {
    if (this.confirmed) return

    // A second submit while the dialog is still up (an impatient double-click; nothing disables
    // the button, because we held the submit back before Turbo could) would open a second
    // confirmation over the same dialog, and one "Send anyway" would then resolve both and send
    // twice. Swallow it instead.
    if (this.asking) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    // Nothing to ask about: a fixed recipient the server already cleared never reaches here, and a
    // dynamic form with no recipient filled in has nothing to check.
    if (!this.hasMessageValue && !this.hasCheckUrlValue) return
    if (this.hasCheckUrlValue && this.recipients().length === 0) return

    // stopImmediatePropagation as well as preventDefault: some of these forms carry a second
    // submit handler (the hospital email widget posts itself), and holding the submit back from
    // Turbo alone would still let that one fire.
    event.preventDefault()
    event.stopImmediatePropagation()

    const submitter = event.submitter || null
    this.asking = true
    let confirmed
    try {
      const message = await this.warning()
      // The server says this recipient is fine after all.
      if (!message) {
        this.asking = false
        this.resubmit(submitter)
        return
      }

      confirmed = await showConfirmModal({
        title: "Check the recipient",
        message,
        confirmLabel: "Send anyway",
        warning: true,
        // Never skippable: a "don't ask me again" on this prompt would put us straight back where
        // the breach started.
        allowSkip: false,
        note: this.correctionNote()
      })
    } finally {
      this.asking = false
    }
    if (!confirmed) return

    this.resubmit(submitter)
  }

  // The sentence to show, or null when there is nothing to warn about. Either the server settled
  // it at render time, or it is asked now -- the rule itself never leaves Ruby.
  async warning() {
    if (!this.hasCheckUrlValue) return this.hasMessageValue ? this.messageValue : null

    try {
      const response = await fetch(this.checkUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({ recipients: this.recipients() })
      })
      if (!response.ok) throw new Error(`check failed: ${response.status}`)

      const body = await response.json()
      return body.unusual ? body.message : null
    } catch {
      // If we can't reach the server we don't know, and "don't know" must not mean "send".
      return [{ text: "Couldn't check this recipient against the provider's usual domains." }]
    }
  }

  resubmit(submitter) {
    this.confirmed = true
    try {
      // requestSubmit re-runs this handler synchronously, which the flag lets through, along with
      // any other submit handler on the form.
      this.element.requestSubmit(submitter)
    } finally {
      this.confirmed = false
    }
  }

  // Where to go if the prompt itself is wrong. A link, not a control: correcting the provider's
  // domains is a deliberate trip to its admin page, not something to do with one click while a
  // send is half-submitted.
  correctionNote() {
    if (!this.hasProviderUrlValue || !this.providerUrlValue) return null

    return {
      text: "Is this a domain that this location regularly uses?",
      linkText: "Add it here ↗",
      href: this.providerUrlValue
    }
  }

  // A typed-in override wins outright: where a form offers both a saved-contact picker and a free
  // text box, the box is what actually gets sent when it's filled in.
  recipients() {
    const overrides = this.readValues(this.recipientOverrideTargets)
    if (overrides.length > 0) return overrides

    return this.readValues(this.recipientTargets)
  }

  readValues(elements) {
    return elements
      .map((element) => {
        if (element.tagName === "SELECT") {
          const option = element.selectedOptions[0]
          if (!option) return ""

          return option.dataset.recipient ?? option.value
        }

        return element.value
      })
      .map((value) => String(value ?? "").trim())
      .filter((value) => value.length > 0)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
